import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cpu, BarChart3, Users, Mail, Gamepad2, Terminal, Film } from 'lucide-react';
import Taskbar from './Taskbar';
import DesktopIcon from './DesktopIcon';
import StartMenu from './StartMenu';
import ContextMenu from './ContextMenu';
import ClippyAI from './ClippyAI';
import Window from './Window';
import WelcomeWindow from './WelcomeWindow';
import WelcomeGuide from './WelcomeGuide';
import Hero from './Hero';
import Services from './Services';
import Benefits from './Benefits';
import About from './About';
import Contact from './Contact';
import ReadmeWindow from './ReadmeWindow';
import RecycleWindow from './RecycleWindow';
import Minesweeper from './Minesweeper';
import CommandPrompt from './CommandPrompt';
import VideoPlayer from './VideoPlayer';
import AppWizard from './AppWizard';
import Winamp from './Winamp';
import {
  MyComputerIcon,
  NetworkIcon,
  InboxIcon,
  RecycleBinIcon,
  InternetIcon,
  WinampIcon,
  GameIcon,
  TerminalIcon,
  VideoIcon,
  ServicesIcon,
  ChartIcon,
  UsersIcon,
  MailIcon,
  SparklesIcon,
  WandIcon,
  DocumentIcon,
  WindowsLogo
} from './Win95Icons';
import { playSound, SOUNDS } from '../App';

// Classic Windows 95 style icons with custom SVG icons
const desktopIcons = [
  // Left column - main apps
  { id: 'mycomputer', label: 'Min Datamaskin', IconComponent: MyComputerIcon, x: 20, y: 20 },
  { id: 'network', label: 'Nettverk', IconComponent: NetworkIcon, x: 20, y: 110 },
  { id: 'welcome', label: 'Velkommen', IconComponent: SparklesIcon, x: 20, y: 200, highlight: true },
  { id: 'recycle', label: 'Papirkurv', IconComponent: RecycleBinIcon, x: 20, y: 290 },
  { id: 'internet', label: 'Internett', IconComponent: InternetIcon, x: 20, y: 380 },
  { id: 'winamp', label: 'Winamp', IconComponent: WinampIcon, x: 20, y: 470 },
  { id: 'appwizard', label: 'App-veiviser', IconComponent: WandIcon, x: 20, y: 560, highlight: true },
  // Second column - info & tools
  { id: 'services', label: 'Tjenester', IconComponent: ServicesIcon, x: 110, y: 20 },
  { id: 'metrics', label: 'Statistikk', IconComponent: ChartIcon, x: 110, y: 110 },
  { id: 'about', label: 'Om Oss', IconComponent: UsersIcon, x: 110, y: 200 },
  { id: 'contact', label: 'Kontakt', IconComponent: MailIcon, x: 110, y: 290 },
  { id: 'readme', label: 'Les Meg', IconComponent: DocumentIcon, x: 110, y: 380 },
  { id: 'video', label: 'Demo Video', IconComponent: VideoIcon, x: 110, y: 470 },
  { id: 'minesweeper', label: 'Minesveiper', IconComponent: GameIcon, x: 110, y: 560 },
  { id: 'cmd', label: 'MS-DOS', IconComponent: TerminalIcon, x: 200, y: 20 },
];

export default function Desktop({ 
  windows, 
  openWindow, 
  closeWindow, 
  minimizeWindow, 
  focusWindow,
  restoreWindow,
  updateWindowPosition,
  volume,
  setVolume,
  onShutdown,
  onBSOD,
  onMatrix,
  crtEnabled,
  setCrtEnabled
}) {
  const [startMenuOpen, setStartMenuOpen] = useState(false);
  const [showClippy, setShowClippy] = useState(false);
  const [clippyDismissed, setClippyDismissed] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [highlightedIcon, setHighlightedIcon] = useState(null);
  const [showWelcomeGuide, setShowWelcomeGuide] = useState(false);

  // Check if first visit and show welcome guide
  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('welcomeGuideShown');
    if (!hasSeenWelcome) {
      // Show welcome guide after intro animation
      const timer = setTimeout(() => setShowWelcomeGuide(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Show Clippy after 15 seconds (only if welcome guide was dismissed)
  useEffect(() => {
    if (!clippyDismissed && !showWelcomeGuide) {
      const timer = setTimeout(() => setShowClippy(true), 15000);
      return () => clearTimeout(timer);
    }
  }, [clippyDismissed, showWelcomeGuide]);

  const handleIconDoubleClick = (id) => {
    openWindow(id);
  };

  const handleIconClick = (id) => {
    setSelectedIcon(id);
  };

  const handleClippyDismiss = () => {
    setShowClippy(false);
    setClippyDismissed(true);
  };

  // Desktop right-click menu
  const handleDesktopContextMenu = useCallback((e) => {
    e.preventDefault();
    playSound(SOUNDS.click, volume / 100 * 0.1);
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        { label: 'Arrange Icons', action: () => {}, disabled: true },
        { label: 'Line up Icons', action: () => {}, disabled: true },
        { divider: true },
        { label: 'Refresh', action: () => window.location.reload(), shortcut: 'F5' },
        { divider: true },
        { 
          label: crtEnabled ? '✓ CRT Effect' : '  CRT Effect', 
          action: () => setCrtEnabled(!crtEnabled) 
        },
        { divider: true },
        { label: 'New Folder', action: () => {}, disabled: true },
        { label: 'Properties', action: () => openWindow('about') },
      ]
    });
  }, [volume, crtEnabled, setCrtEnabled, openWindow]);

  // Icon right-click menu
  const handleIconContextMenu = useCallback((e, iconId) => {
    e.preventDefault();
    e.stopPropagation();
    playSound(SOUNDS.click, volume / 100 * 0.1);
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        { label: 'Open', action: () => openWindow(iconId) },
        { divider: true },
        { label: 'Cut', action: () => {}, disabled: true },
        { label: 'Copy', action: () => {}, disabled: true },
        { divider: true },
        { label: 'Create Shortcut', action: () => {}, disabled: true },
        { label: 'Delete', action: () => {}, disabled: true },
        { label: 'Rename', action: () => {}, disabled: true },
        { divider: true },
        { label: 'Properties', action: () => {} },
      ]
    });
  }, [volume, openWindow]);

  const handleDesktopClick = (e) => {
    if (e.target === e.currentTarget) {
      setSelectedIcon(null);
      setStartMenuOpen(false);
    }
  };

  const openWindows = Object.entries(windows)
    .filter(([_, w]) => w.open)
    .map(([id]) => id);

  const windowLabels = {
    welcome: 'START HERE',
    readme: 'README.txt',
    services: 'services.exe',
    metrics: 'metrics.sys',
    about: 'about.txt',
    contact: 'contact.msg',
    recycle: 'Recycle Bin',
    minesweeper: 'Minesweeper',
    cmd: 'Command Prompt',
    video: 'Media Player',
    appwizard: 'App Builder',
    winamp: 'Winamp',
    mycomputer: 'My Computer',
    network: 'Network',
    internet: 'Internet Explorer'
  };

  return (
    <motion.div 
      className="win95-desktop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={handleDesktopClick}
      onContextMenu={handleDesktopContextMenu}
    >
      {/* CRT Overlay */}
      {crtEnabled && (
        <>
          <div className="crt-overlay" />
          <div className="crt-flicker" />
        </>
      )}

      {/* Desktop Icons */}
      <div className="desktop-icons">
        {desktopIcons.map(icon => (
          <DesktopIcon
            key={icon.id}
            id={icon.id}
            label={icon.label}
            IconComponent={icon.IconComponent}
            x={icon.x}
            y={icon.y}
            selected={selectedIcon === icon.id}
            highlight={icon.highlight}
            glowing={highlightedIcon === icon.id}
            onDoubleClick={() => handleIconDoubleClick(icon.id)}
            onClick={() => handleIconClick(icon.id)}
            onContextMenu={(e) => handleIconContextMenu(e, icon.id)}
          />
        ))}
      </div>

      {/* Welcome Guide for first-time visitors */}
      <AnimatePresence>
        {showWelcomeGuide && (
          <WelcomeGuide
            onOpenWindow={(id) => {
              openWindow(id);
              setShowWelcomeGuide(false);
            }}
            onDismiss={() => setShowWelcomeGuide(false)}
            onHighlightIcon={setHighlightedIcon}
            isFirstVisit={!localStorage.getItem('welcomeGuideShown')}
          />
        )}
      </AnimatePresence>

      {/* Windows */}
      <AnimatePresence>
        {windows.welcome?.open && !windows.welcome?.minimized && (
          <WelcomeWindow
            zIndex={windows.welcome.zIndex}
            onClose={() => closeWindow('welcome')}
            onMinimize={() => minimizeWindow('welcome')}
            onFocus={() => focusWindow('welcome')}
            position={windows.welcome.position}
            onPositionChange={(pos) => updateWindowPosition('welcome', pos)}
            onOpenContact={() => openWindow('contact')}
            onOpenServices={() => openWindow('services')}
          />
        )}

        {windows.readme?.open && !windows.readme?.minimized && (
          <ReadmeWindow
            zIndex={windows.readme.zIndex}
            onClose={() => closeWindow('readme')}
            onMinimize={() => minimizeWindow('readme')}
            onFocus={() => focusWindow('readme')}
            position={windows.readme.position}
            onPositionChange={(pos) => updateWindowPosition('readme', pos)}
          />
        )}

        {windows.services?.open && !windows.services?.minimized && (
          <Window
            id="services"
            title="services.exe"
            icon={Cpu}
            zIndex={windows.services.zIndex}
            onClose={() => closeWindow('services')}
            onMinimize={() => minimizeWindow('services')}
            onFocus={() => focusWindow('services')}
            position={windows.services.position}
            onPositionChange={(pos) => updateWindowPosition('services', pos)}
            width={800}
            height={500}
          >
            <Services />
          </Window>
        )}

        {windows.metrics?.open && !windows.metrics?.minimized && (
          <Window
            id="metrics"
            title="metrics.sys"
            icon={BarChart3}
            zIndex={windows.metrics.zIndex}
            onClose={() => closeWindow('metrics')}
            onMinimize={() => minimizeWindow('metrics')}
            onFocus={() => focusWindow('metrics')}
            position={windows.metrics.position}
            onPositionChange={(pos) => updateWindowPosition('metrics', pos)}
            width={900}
            height={450}
          >
            <Benefits />
          </Window>
        )}

        {windows.about?.open && !windows.about?.minimized && (
          <Window
            id="about"
            title="about.txt"
            icon={Users}
            zIndex={windows.about.zIndex}
            onClose={() => closeWindow('about')}
            onMinimize={() => minimizeWindow('about')}
            onFocus={() => focusWindow('about')}
            position={windows.about.position}
            onPositionChange={(pos) => updateWindowPosition('about', pos)}
            width={850}
            height={500}
          >
            <About />
          </Window>
        )}

        {windows.contact?.open && !windows.contact?.minimized && (
          <Window
            id="contact"
            title="contact.msg"
            icon={Mail}
            zIndex={windows.contact.zIndex}
            onClose={() => closeWindow('contact')}
            onMinimize={() => minimizeWindow('contact')}
            onFocus={() => focusWindow('contact')}
            position={windows.contact.position}
            onPositionChange={(pos) => updateWindowPosition('contact', pos)}
            width={600}
            height={650}
          >
            <Contact />
          </Window>
        )}

        {windows.recycle?.open && !windows.recycle?.minimized && (
          <RecycleWindow
            zIndex={windows.recycle.zIndex}
            onClose={() => closeWindow('recycle')}
            onMinimize={() => minimizeWindow('recycle')}
            onFocus={() => focusWindow('recycle')}
            position={windows.recycle.position}
            onPositionChange={(pos) => updateWindowPosition('recycle', pos)}
          />
        )}

        {windows.minesweeper?.open && !windows.minesweeper?.minimized && (
          <Window
            id="minesweeper"
            title="Minesweeper"
            icon={Gamepad2}
            zIndex={windows.minesweeper.zIndex}
            onClose={() => closeWindow('minesweeper')}
            onMinimize={() => minimizeWindow('minesweeper')}
            onFocus={() => focusWindow('minesweeper')}
            position={windows.minesweeper.position}
            onPositionChange={(pos) => updateWindowPosition('minesweeper', pos)}
            width={280}
            height={380}
            resizable={false}
          >
            <Minesweeper />
          </Window>
        )}

        {windows.cmd?.open && !windows.cmd?.minimized && (
          <Window
            id="cmd"
            title="Command Prompt"
            icon={Terminal}
            zIndex={windows.cmd.zIndex}
            onClose={() => closeWindow('cmd')}
            onMinimize={() => minimizeWindow('cmd')}
            onFocus={() => focusWindow('cmd')}
            position={windows.cmd.position}
            onPositionChange={(pos) => updateWindowPosition('cmd', pos)}
            width={650}
            height={400}
          >
            <CommandPrompt 
              onMatrix={onMatrix}
              onClose={() => closeWindow('cmd')}
            />
          </Window>
        )}

        {windows.video?.open && !windows.video?.minimized && (
          <Window
            id="video"
            title="Media Player - Lexy CTO 5.mp4"
            icon={Film}
            zIndex={windows.video.zIndex}
            onClose={() => closeWindow('video')}
            onMinimize={() => minimizeWindow('video')}
            onFocus={() => focusWindow('video')}
            position={windows.video.position}
            onPositionChange={(pos) => updateWindowPosition('video', pos)}
            width={560}
            height={420}
          >
            <VideoPlayer />
          </Window>
        )}

        {windows.appwizard?.open && !windows.appwizard?.minimized && (
          <AppWizard
            zIndex={windows.appwizard.zIndex}
            onClose={() => closeWindow('appwizard')}
            onMinimize={() => minimizeWindow('appwizard')}
            onFocus={() => focusWindow('appwizard')}
            position={windows.appwizard.position}
            onPositionChange={(pos) => updateWindowPosition('appwizard', pos)}
            openContact={() => openWindow('contact')}
          />
        )}

        {windows.winamp?.open && !windows.winamp?.minimized && (
          <Winamp
            zIndex={windows.winamp.zIndex}
            onClose={() => closeWindow('winamp')}
            onMinimize={() => minimizeWindow('winamp')}
            onFocus={() => focusWindow('winamp')}
            position={windows.winamp.position}
            onPositionChange={(pos) => updateWindowPosition('winamp', pos)}
          />
        )}
      </AnimatePresence>

      {/* Clippy AI Easter Egg */}
      <AnimatePresence>
        {showClippy && (
          <ClippyAI 
            onDismiss={handleClippyDismiss} 
            onOpenContact={() => openWindow('contact')} 
          />
        )}
      </AnimatePresence>

      {/* Context Menu */}
      <AnimatePresence>
        {contextMenu && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            items={contextMenu.items}
            onClose={() => setContextMenu(null)}
          />
        )}
      </AnimatePresence>

      {/* Start Menu */}
      <AnimatePresence>
        {startMenuOpen && (
          <StartMenu 
            onClose={() => setStartMenuOpen(false)}
            onOpenWindow={(id) => {
              openWindow(id);
              setStartMenuOpen(false);
            }}
            onShutdown={onShutdown}
            onShowHelp={() => {
              // Dispatch keyboard event to show help
              window.dispatchEvent(new KeyboardEvent('keydown', { key: '?' }));
            }}
            crtEnabled={crtEnabled}
            setCrtEnabled={setCrtEnabled}
            volume={volume}
            setVolume={setVolume}
          />
        )}
      </AnimatePresence>

      {/* Taskbar */}
      <Taskbar 
        onStartClick={() => setStartMenuOpen(!startMenuOpen)}
        startMenuOpen={startMenuOpen}
        openWindows={openWindows}
        windows={windows}
        windowLabels={windowLabels}
        onWindowClick={restoreWindow}
        volume={volume}
        setVolume={setVolume}
        onBSOD={onBSOD}
      />
    </motion.div>
  );
}
